import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  deleteTickets,
  getTickets,
  pushTicketsBatch,
  TicketDetail,
  updateTicket,
  isDateInValidPeriod,
} from "../services/ticketApiService";
import { useFinancialPeriods } from "../hooks/useFinancialPeriods";
import { buildLinqFilter, buildLinqSort } from "../services/queryBuilder";
import {
  TableAction,
  TanStackDynamicTable,
} from "../components/TanStackDynamicTable";
import {
  ColumnDef,
  ColumnFiltering,
  ColumnFiltersState,
  SortingState,
} from "@tanstack/react-table";
import { Edit, FileStack, Search, Send, Trash2, X } from "lucide-react";
import { formatTimestamp } from "../utils/formatters";
import { useNavigate } from "react-router-dom";
import { CustomSelect } from "../components/CustomSelect";
import { createPortal } from "react-dom";
import { TicketStats } from "../components/TicketStats";
import { useFilterParams } from "../hooks/useFilterParams";

const PendingPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const financialPeriods = useFinancialPeriods();
  const { getInitialFilters, syncToUrl } = useFilterParams("pending_v2_filters", (filters) => {
    setTableState((prev) => ({ ...prev, columnFilters: filters, pagination: { ...prev.pagination, pageIndex: 0 } }));
  });
  const [data, setData] = useState<TicketDetail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [ticketsToDelete, setTicketsToDelete] = useState<string[]>([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [ticketsToAssign, setTicketsToAssign] = useState<string[]>([]);

  const [tableState, setTableState] = useState<{
    sorting: SortingState;
    pagination: any;
    columnFilters: ColumnFiltersState;
  }>({
    sorting: JSON.parse(localStorage.getItem(`pending_v2_sorting`) || "[]"),
    pagination: { pageIndex: 0, pageSize: 100 },
    columnFilters: getInitialFilters(),
  });

  useEffect(() => {
    syncToUrl(tableState.columnFilters);
  }, [tableState.columnFilters]);

  const columns = useMemo<ColumnDef<TicketDetail>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <div className="px-1">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
              checked={table.getIsAllPageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
            />
          </div>
        ),
        size: 20,
      },
      {
        id: "ticketNumber",
        header: "Ticket #",
        accessorKey: "ticketNumber",
        meta: { type: "text" },
        cell: ({ row, getValue }) => {
          const ticketId = row.original.id; // Or whatever your internal DB ID property is
          const displayValue = getValue() as string;
          return (
            <Link
              to={`/ticket/${ticketId}`}
              className="text-primary hover:text-blue-600 dark:hover:text-blue-400 hover:underline font-bold transition-colors"
              // Stop propagation if clicking the link accidentally triggers row selection
              onClick={(e) => e.stopPropagation()}
            >
              {displayValue}
            </Link>
          );
        },
      },
      {
        id: "orderDate",
        header: "Order Date",
        accessorKey: "orderDate",
        meta: { type: "date" },
        filterFn: () => true,
        cell: ({ getValue }) => {
          const dateStr = getValue() as string | null;
          const invalid = !isDateInValidPeriod(dateStr, financialPeriods);
          return (
            <span className={invalid ? "px-1.5 py-0.5 rounded bg-red-50 border border-red-400 text-red-700 dark:bg-red-500/10 dark:border-red-500/50 dark:text-red-400" : ""}>
              {dateStr?.substring(0, 10) || ""}
            </span>
          );
        },
      },
      {
        id: "vendorID",
        header: "Supplier",
        accessorKey: "vendorID",
        meta: { type: "text" },
      },
      {
        id: "inventoryID",
        header: "Material",
        accessorKey: "inventoryID",
        meta: { type: "text" },
      },
      {
        id: "description",
        header: "Description",
        accessorKey: "description",
        meta: { type: "text" },
      },
      {
        id: "receivedQty",
        header: "Quantity",
        accessorKey: "receivedQty",
        meta: { type: "number" },
        filterFn: () => true,
      },
      { id: "uom", header: "UOM", accessorKey: "uom", meta: { type: "text" } },
      {
        id: "warehouse",
        header: "Plant",
        accessorKey: "warehouse",
        meta: { type: "text" },
      },
      {
        id: "siteid",
        header: "Quarry",
        accessorKey: "siteid",
        meta: { type: "text" },
      },
      {
        id: "branch",
        header: "Branch",
        accessorKey: "branch",
        meta: { type: "text" },
      },
      {
        id: "orderType",
        header: "Order Type",
        accessorKey: "orderType",
        meta: { type: "text" },
      },
      {
        id: "postPeriod",
        header: "Post Period",
        accessorKey: "postPeriod",
        meta: { type: "text" },
      },
      {
        id: "freightVendorID",
        header: "Hauler",
        accessorKey: "freightVendorID",
        meta: { type: "text" },
      },
      {
        id: "cgoProjectID",
        header: "CGO Project",
        accessorKey: "cgoProjectID",
        meta: { type: "text" },
      },
      {
        id: "blanketPurchaseOrder",
        header: "Blanket PO",
        accessorKey: "blanketPurchaseOrder",
        meta: { type: "text" },
      },
      {
        id: "truckID",
        header: "Truck",
        accessorKey: "truckID",
        meta: { type: "text" },
      },
      {
        id: "receivedTimestamp",
        header: "Received Timestamp",
        accessorKey: "receivedTimestamp",
        meta: { type: "datetime" },
        filterFn: () => true,
        cell: ({ getValue }) => {
          const rawValue = getValue() as string;
          return formatTimestamp(rawValue);
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        meta: { type: "text" },
        cell: ({ getValue }) => {
          // 1. Grab the raw status string from the row
          const status = getValue() as string;

          // 2. Return your styled badge
          return (
            <span
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                status === "Pending"
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                  : status === "Pushed"
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                    : status === "Deleted"
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400"
                      : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
              }`}
            >
              {status}
            </span>
          );
        },
      },
      {
        id: "batchId",
        header: "Batch",
        accessorKey: "batchId",
        meta: { type: "text" },
        // cell: ({ getValue }) => {
        //   const rawValue = getValue() as string;
        //   return encodeGUID(rawValue);
        // },
      },
    ],
    [financialPeriods],
  );

  const handleBatchPush = async (selectedRows: any[]) => {
    if (!accounts[0]) return;

    const ids = selectedRows.map((row) => row.id);
    if (ids.length === 0) return;

    if (
      !window.confirm(
        `Are you sure you want to push ${ids.length} tickets to the Acumatica?`,
      )
    ) {
      return;
    }

    try {
      setIsLoading(true);

      const res = await pushTicketsBatch(instance, accounts[0], ids);
      const results = res.results || [];
      const successes = results.filter((r: any) => r.success);
      const failures = results.filter((r: any) => !r.success);
      let alertMsg = `Push Results:\n✅ ${successes.length} Successful\n❌ ${failures.length} Failed`;

      if (failures.length > 0) {
        alertMsg +=
          `\n\nErrors:\n` +
          failures
            .map((f: any) => `Ticket ${f.ticketId}: ${f.error}`)
            .join("\n");
      }

      window.alert(alertMsg);
      await fetchData();
    } catch (error) {
      console.error("Error pushing tickets:", error);
      alert("Failed to push tickets to ERP. Check the console for details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchDeleteClick = (selectedRows: any[]) => {
    const ids = selectedRows.map((row) => row.id.toString());
    if (ids.length === 0) return;

    setTicketsToDelete(ids);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async (reason: string) => {
    if (!accounts[0] || ticketsToDelete.length === 0) return;

    setIsDeleteModalOpen(false);
    setIsLoading(true);

    try {
      await deleteTickets(instance, accounts[0], ticketsToDelete, reason);
      window.alert(
        `✅ Successfully deleted ${ticketsToDelete.length} ticket(s).`,
      );
      await fetchData();
      setTicketsToDelete([]);
    } catch (error) {
      console.error("Error deleting tickets:", error);
      alert("Failed to delete tickets. Check console.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignBatch = (selectedRows: any[]) => {
    const ids = selectedRows.map((row) => row.id.toString());
    if (ids.length === 0) return;

    setTicketsToAssign(ids);
    setIsAssignModalOpen(true);
  };

  const confirmAssign = async (batchid: string) => {
    if (!accounts[0] || ticketsToAssign.length === 0) return;

    setIsAssignModalOpen(false);
    setIsLoading(true);

    try {
      const updatePromises = ticketsToAssign.map((ticketId) => {
        return updateTicket(instance, accounts[0], ticketId, {
          batchId: batchid,
        });
      });

      await Promise.all(updatePromises);
      window.alert(
        `✅ Successfully assigned ${ticketsToAssign.length} ticket(s).`,
      );
      await fetchData();
      setTicketsToAssign([]);
    } catch (error) {
      console.error("Error assigning tickets:", error);
      alert("Failed to assign tickets. Check console.");
    } finally {
      setIsLoading(false);
    }
  };

  const tableActions: TableAction[] = [
    {
      label: "Edit",
      icon: <Edit size={16} className="text-blue-500" />,
      onClick: (selectedRows) => {
        const ids = selectedRows.map((row) => row.id);

        if (ids.length === 0) return;

        const queueKey = `queue_${Date.now()}`;
        sessionStorage.setItem(queueKey, JSON.stringify({ queue: ids, index: 0, returnPath: "/pending" }));
        navigate(`/ticket/${ids[0]}?queueKey=${queueKey}`);
      },
    },
    {
      label: "Push",
      icon: <Send size={16} className="text-emerald-500" />,
      onClick: handleBatchPush,
    },
    {
      label: "Delete",
      icon: <Trash2 size={16} className="text-red-500" />,
      onClick: handleBatchDeleteClick,
    },
    {
      label: "Assign Batch",
      icon: <FileStack size={16} className="text-purple-500" />,
      onClick: handleAssignBatch,
    },
  ];

  const getCustomRowStyles = (row: TicketDetail) => {
    if (row.duplicatedTicketNumber) {
      return "!bg-red-50/50 dark:!bg-red-900/10 hover:!bg-red-50 dark:hover:!bg-red-900/30 !border-red-100 dark:!border-red-800/50 !text-red-800 dark:!text-red-200";
    }
    return "";
  };

  const fetchData = async () => {
    if (!accounts[0]) return;
    setIsLoading(true);

    const filter = buildLinqFilter(
      "Pending",
      tableState.columnFilters,
    );
    const sort = buildLinqSort(tableState.sorting);
    const result = await getTickets(
      instance,
      accounts[0],
      filter,
      sort,
      tableState.pagination.pageIndex + 1,
      tableState.pagination.pageSize,
    );

    setData(result.data);
    setTotalCount(result.totalCount);
    setIsLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData();
    }, 400);
    return () => {
      clearTimeout(handler);
    };
  }, [
    tableState.columnFilters,
    tableState.sorting,
    tableState.pagination,
  ]);

  return (
    <div className="p-6 h-full flex flex-col">
      <h1 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">
        Pending Tickets
      </h1>
      <TanStackDynamicTable
        data={data}
        columns={columns}
        rowCount={totalCount}
        tableName="pending_v2"
        defaultPageSize={100}
        showPageSizeSelector={false}
        initialColumnFilters={tableState.columnFilters}
        onStateChange={setTableState}
        actions={tableActions}
        getRowClassName={getCustomRowStyles}
        renderHeaderStats={(selectedRows) => (
          <TicketStats
            selectedRows={selectedRows}
            totalCount={totalCount}
          ></TicketStats>
        )}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        ticketCount={ticketsToDelete.length}
      />
      <AssignConfirmationModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onConfirm={confirmAssign}
        ticketCount={ticketsToAssign.length}
      />
    </div>
  );
};

export default PendingPage;

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  ticketCount,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  ticketCount: number;
}) => {
  const [reason, setReason] = useState("Duplicate");
  const [otherReason, setOtherReason] = useState("");

  const deleteOptions = [
    { label: "Duplicate", value: "Duplicate" },
    { label: "Test Page", value: "Test Page" },
    { label: "Incorrect Data", value: "Incorrect Data" },
    { label: "Other", value: "Other" },
  ];

  const handleConfirm = () => {
    if (reason === "Other") {
      if (otherReason.trim()) {
        onConfirm(otherReason);
      } else {
        alert("Please provide a reason for deletion.");
      }
    } else {
      onConfirm(reason);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-700">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
          <h3 className="font-bold text-lg text-slate-900 dark:text-white">
            Confirm Deletion
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4 text-slate-700 dark:text-slate-300">
          <p>
            Are you sure you want to delete{" "}
            <span className="font-bold text-red-500">{ticketCount}</span>{" "}
            selected ticket(s)? Please provide a reason.
          </p>
          <CustomSelect
            value={reason}
            onChange={setReason}
            options={deleteOptions}
          />
          {reason === "Other" && (
            <input
              type="text"
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="Please specify a reason"
              className="w-full rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2.5 text-sm focus:ring-primary focus:border-primary text-slate-900 dark:text-white outline-none"
            />
          )}
        </div>
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-2 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors shadow-lg shadow-rose-500/20"
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const AssignConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  ticketCount,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (batchid: string) => void;
  ticketCount: number;
}) => {
  const [reason, setReason] = useState("");

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-700">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
          <h3 className="font-bold text-lg text-slate-900 dark:text-white">
            Enter Batch Id
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4 text-slate-700 dark:text-slate-300">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter batch id.."
            className="w-full rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2.5 text-sm focus:ring-primary focus:border-primary text-slate-900 dark:text-white outline-none"
          />
        </div>
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-2 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary-700 rounded-lg transition-colors shadow-lg shadow-primary-500/20"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
